import { FileText, Search, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

interface ExtractedTextPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  extractedText: string;
  onProceed: () => void;
  isProceedDisabled?: boolean;
}

export const ExtractedTextPreviewModal: React.FC<
  ExtractedTextPreviewModalProps
> = ({
  isOpen,
  onClose,
  fileName,
  extractedText,
  onProceed,
  isProceedDisabled,
}) => {
  const [search, setSearch] = useState('');

  const lines = useMemo(() => extractedText.split('\n'), [extractedText]);

  const filteredLines = useMemo(() => {
    if (!search.trim()) return lines;
    const lower = search.toLowerCase();
    return lines.filter((line) => line.toLowerCase().includes(lower));
  }, [lines, search]);

  const stats = useMemo(() => {
    const nonEmpty = lines.filter((l) => l.trim()).length;
    return {
      chars: extractedText.length.toLocaleString(),
      lines: nonEmpty,
      filtered: search.trim() ? filteredLines.length : null,
    };
  }, [extractedText, lines, filteredLines, search]);

  const highlightMatch = (line: string) => {
    if (!search.trim()) return line;
    const idx = line.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return line;
    return (
      <>
        {line.slice(0, idx)}
        <mark className="bg-yellow-200 text-yellow-900 rounded px-0.5">
          {line.slice(idx, idx + search.length)}
        </mark>
        {line.slice(idx + search.length)}
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop — no onClick to prevent close on outside click */}
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-2xl flex flex-col max-h-[88vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FileText size={20} className="text-pos-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Extracted Text Preview
              </h3>
              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                {fileName}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close preview"
          >
            <X size={20} />
          </button>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 px-6 py-2.5 bg-gray-50 border-b border-gray-100 flex-shrink-0">
          <span className="text-xs text-gray-500">
            <span className="font-semibold text-gray-700">{stats.chars}</span>{' '}
            characters
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-xs text-gray-500">
            <span className="font-semibold text-gray-700">{stats.lines}</span>{' '}
            non-empty lines
          </span>
          {stats.filtered !== null && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-xs text-amber-600">
                <span className="font-semibold">{stats.filtered}</span> matches
              </span>
            </>
          )}
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search extracted text..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-4 custom-scrollbar">
          {filteredLines.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No lines match your search.
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-400 pb-2 w-12 pr-4 border-b border-gray-100">
                    #
                  </th>
                  <th className="text-left text-xs font-medium text-gray-400 pb-2 border-b border-gray-100">
                    Content
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredLines.map((line, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={idx} className="group hover:bg-gray-50">
                    <td className="py-1 pr-4 text-xs text-gray-300 font-mono align-top w-12 select-none">
                      {idx + 1}
                    </td>
                    <td className="py-1 font-mono text-xs text-gray-700 break-all whitespace-pre-wrap align-top">
                      {line.trim() ? (
                        highlightMatch(line)
                      ) : (
                        <span className="text-gray-200">&nbsp;</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0 rounded-b-2xl">
          <p className="text-xs text-gray-500">
            Review the content above, then proceed to let AI extract menu items.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-white transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={onProceed}
              disabled={isProceedDisabled}
              className="px-5 py-2 text-sm bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Extract with AI
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
